import { useCallback, useEffect, useMemo, useState } from 'react';
import { defaultSettings } from '@/lib/bracket/layout';
import { PT_PX } from '@/lib/bracket/paper';

// Only explicit user choices are persisted, so per-bracket smart defaults
// (e.g. mirrored for 8+ teams) still apply everywhere else.
export function useBracketSettings(key, model, base) {
  const defaults = useMemo(() => ({ ...defaultSettings(model), ...(base || {}) }), [model, base]);
  const [overrides, setOverrides] = useState(() => {
    try { return JSON.parse(localStorage.getItem(key) || '{}'); } catch { return {}; }
  });
  const update = useCallback((patch) => setOverrides((prev) => {
    const next = { ...prev, ...patch };
    try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* storage full or disabled */ }
    return next;
  }), [key]);
  return [{ ...defaults, ...overrides }, update];
}

export function useFontsReady() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let on = true;
    const loads = [400, 600, 700].map((w) => document.fonts?.load(`${w} 12px "Barlow Condensed"`));
    Promise.all(loads).catch(() => {}).finally(() => { if (on) setReady(true); });
    return () => { on = false; };
  }, []);
  return ready;
}

export function useViewerZoom(ref, page) {
  const [mode, setMode] = useState('width');
  const [manual, setManual] = useState(PT_PX);
  const [box, setBox] = useState({ w: 900, h: 800 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([e]) => setBox({ w: e.contentRect.width, h: window.innerHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  const fitW = Math.max(0.2, (box.w - 32) / page.w);
  const fitP = Math.max(0.2, Math.min(fitW, (box.h - 240) / page.h));
  const z = mode === 'width' ? Math.min(fitW, 2.6) : mode === 'page' ? fitP : manual;
  const zoomBy = (f) => { setManual(Math.min(6, Math.max(0.25, z * f))); setMode('manual'); };
  return { z, mode, setMode, zoomBy, pct: Math.round((z / PT_PX) * 100) };
}
